/**
 * Bot profile (Movement spec §7 / Arena §Bots). Personality and tier become NUMBERS
 * that weight one shared behaviour tree — never a separate code path per personality.
 * If a decision needs `if (personality === 'camper')`, the weight is missing here.
 */

import { TIER_COMBAT } from './combat';
import { TIER_SIGMA } from '../nav/postprocess';
import { rollReaction } from '../nav/perception';
import type { SkillTier, Personality } from './names';

export interface BotProfile {
  tier: SkillTier;
  personality: Personality;
  // Tier-derived.
  reactionMs: number;
  missChance: number;
  damage: number;
  range: number;
  cooldownMs: number;
  pathNoiseSigma: number;
  /**
   * Extra, RE-ROLLED-per-decision latency on top of reactionMs. `reactionMs` is a
   * constant for the whole match, so once a bot had seen you it tracked you forever
   * with zero lag; the jitter is what makes its responses uneven the way a person's
   * are. Scaled off the tier, so Rookies dither and Elites barely do.
   */
  decisionJitterMs: number;
  // Personality-derived weights.
  cautionWeight: number;    // multiplies the danger map in retreat path cost
  fleeHpPct: number;        // retreat below this HP fraction
  roadPreference: number;
  chokepointAffinity: number;
  greed: number;            // how long it holds pendingGold before banking (0..1)
  aggression: number;       // how strongly it seeks fights
  targetHpWeight: number;   // how much it prefers finishing the wounded
  /** How long it stays on a chosen target before re-ranking (ms). Kills the 5 Hz flip-flop. */
  commitmentMs: number;
  /** Per-decision chance of deliberately taking the SECOND-best target and sticking with it. */
  blunderChance: number;
  /** Multiplier on how much the aim penalties in rollHit() bite. Wild swingers > 1. */
  aimError: number;
}

interface PWeights {
  cautionWeight: number; fleeHpPct: number; chokepointAffinity: number;
  greed: number; aggression: number; targetHpWeight: number; roadPreference: number;
  commitmentMs: number; blunderChance: number; aimError: number;
}

// Spec §Bots personalities (Aggressor…Coward), expressed purely as weights.
const PERSONALITY: Record<Personality, PWeights> = {
  // commitmentMs / blunderChance / aimError are what make the five read as different
  // PEOPLE rather than five copies with different thresholds: the Aggressor locks on
  // hard and swings wild, the Tactician re-evaluates coolly and rarely misjudges, the
  // Coward drops a fight the moment it sours.
  aggressor: { cautionWeight: 0.2, fleeHpPct: 0.15, chokepointAffinity: 0.2, greed: 0.9, aggression: 1.0, targetHpWeight: 0.5, roadPreference: 0.7, commitmentMs: 4000, blunderChance: 0.12, aimError: 1.35 },
  farmer: { cautionWeight: 1.0, fleeHpPct: 0.35, chokepointAffinity: 0.4, greed: 0.2, aggression: 0.6, targetHpWeight: 1.0, roadPreference: 0.9, commitmentMs: 2600, blunderChance: 0.08, aimError: 1.0 },
  camper: { cautionWeight: 1.2, fleeHpPct: 0.40, chokepointAffinity: 0.9, greed: 0.5, aggression: 0.5, targetHpWeight: 0.7, roadPreference: 0.8, commitmentMs: 3000, blunderChance: 0.07, aimError: 0.95 },
  tactician: { cautionWeight: 1.5, fleeHpPct: 0.40, chokepointAffinity: 0.9, greed: 0.5, aggression: 0.6, targetHpWeight: 0.8, roadPreference: 1.0, commitmentMs: 2200, blunderChance: 0.05, aimError: 0.85 },
  coward: { cautionWeight: 2.5, fleeHpPct: 0.60, chokepointAffinity: 0.6, greed: 0.2, aggression: 0.3, targetHpWeight: 0.6, roadPreference: 1.0, commitmentMs: 1500, blunderChance: 0.10, aimError: 1.15 },
};

/** Build a full profile from tier + personality (rng resolves the reaction band). */
export function deriveProfile(tier: SkillTier, personality: Personality, rng: () => number): BotProfile {
  const c = TIER_COMBAT[tier];
  const p = PERSONALITY[personality];
  const reactionMs = rollReaction(tier, rng());
  return {
    tier, personality,
    reactionMs,
    missChance: c.missChance,
    damage: c.damage,
    range: c.range,
    cooldownMs: c.cooldownMs,
    pathNoiseSigma: TIER_SIGMA[tier] ?? 0.25,
    decisionJitterMs: reactionMs * 0.6,
    ...p,
  };
}
